import React, { useState, useContext, useEffect } from 'react'
import { Input, Modal, Form, Select, DatePicker, Radio, Switch } from 'antd'
import api from '../../services/api'
import SL_Button from '../../components/buttons/Button'
import GeneralContext from '../../context/GeneralContext/GeneralContext'
import dayjs from 'dayjs'

const { Option } = Select

const DAYS_OF_WEEK = [
    { value: 0, label: 'Domingo' },
    { value: 1, label: 'Segunda-feira' },
    { value: 2, label: 'Terça-feira' },
    { value: 3, label: 'Quarta-feira' },
    { value: 4, label: 'Quinta-feira' },
    { value: 5, label: 'Sexta-feira' },
    { value: 6, label: 'Sábado' },
]

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2)
    const minute = i % 2 === 0 ? '00' : '30'
    return `${String(hour).padStart(2, '0')}:${minute}`
})

export default function EditScheduledNotification({ schedule, handleOnCloseModal }) {
    const [form] = Form.useForm()
    const [usersList, setUsersList] = useState([])
    const [scheduleType, setScheduleType] = useState(schedule?.schedule_type || 'weekly')

    const { setShowLoading } = useContext(GeneralContext)

    useEffect(() => {
        loadUsersList()
    }, [])

    async function loadUsersList() {
        try {
            const { data } = await api.user.list()
            setUsersList(data)

            // Preenche os usuários já selecionados após carregar a lista
            if (schedule?.user_ids?.length > 0) {
                // Aguarda a lista carregar para montar os values no formato id|nome
                const matchedUsers = data
                    .filter(u => schedule.user_ids.includes(u.id))
                    .map(u => `${u.id}|${u.full_name}`)
                form.setFieldValue('users', matchedUsers)
            }
        } catch (err) {
            console.log(err)
        }
    }

    const handleUpdate = async (values) => {
        setShowLoading(true)
        try {
            const formatedUsers = values?.users?.length > 0
                ? values.users.map(u => {
                    // Pode vir como "id|nome" (novo) ou só id (já era number)
                    const parsed = parseInt(String(u).split('|')[0])
                    return isNaN(parsed) ? u : parsed
                })
                : []

            const body = {
                title: values.title,
                description: values.description,
                schedule_type: values.schedule_type,
                scheduled_time: values.scheduled_time,
                day_of_week: values.schedule_type === 'weekly' ? values.day_of_week : null,
                specific_date: values.schedule_type === 'specific'
                    ? dayjs(values.specific_date).format('YYYY-MM-DD')
                    : null,
                user_ids: formatedUsers,
                is_active: values.is_active,
            }

            await api.scheduledNotifications.update(schedule.id, body)
            await handleOnCloseModal(true)
        } finally {
            setShowLoading(false)
        }
    }

    const initialValues = {
        title: schedule?.title,
        description: schedule?.description,
        schedule_type: schedule?.schedule_type || 'weekly',
        day_of_week: schedule?.day_of_week,
        scheduled_time: schedule?.scheduled_time,
        specific_date: schedule?.specific_date ? dayjs(schedule.specific_date) : null,
        is_active: schedule?.is_active ?? true,
        // users preenchido dinamicamente após loadUsersList
    }

    return (
        <Modal
            title={<div className="text-xl font-bold text-fit-red-900">Editar Agendamento de Notificação</div>}
            open={true}
            onCancel={() => handleOnCloseModal(false)}
            centered={true}
            footer={
                <div className='flex flex-col justify-end ss:flex-row gap-2'>
                    <SL_Button className="w-full ss:w-[150px] bg-gray-500 border-transparent" onClick={() => handleOnCloseModal(false)}>Fechar</SL_Button>
                    <SL_Button className="w-full ss:w-[150px] m-0" onClick={() => form.submit()}>Salvar</SL_Button>
                </div>
            }
            maskClosable={false}
            width={650}
            bodyStyle={{ overflow: 'auto', maxHeight: 540 }}
        >
            <Form
                onFinish={handleUpdate}
                layout="vertical"
                requiredMark='optional'
                form={form}
                initialValues={initialValues}
            >
                <div className='flex flex-col gap-3'>

                    <Form.Item
                        label={<span className='text-fit-red-900 font-semibold'>Título:</span>}
                        name="title"
                        className="w-full m-0"
                        rules={[{ required: true, message: 'O título é obrigatório.' }]}
                    >
                        <Input className='text-fit-red-900' />
                    </Form.Item>

                    <Form.Item
                        label={<span className='text-fit-red-900 font-semibold'>Mensagem:</span>}
                        name="description"
                        className="w-full m-0"
                        rules={[{ required: true, message: 'A mensagem é obrigatória.' }]}
                    >
                        <Input.TextArea rows={3} className='text-fit-red-900' />
                    </Form.Item>

                    <Form.Item
                        label={<span className='text-fit-red-900 font-semibold'>Tipo de agendamento:</span>}
                        name="schedule_type"
                        className="w-full m-0"
                    >
                        <Radio.Group onChange={(e) => setScheduleType(e.target.value)}>
                            <Radio value="weekly">Semanal (dia da semana)</Radio>
                            <Radio value="specific">Data específica</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {scheduleType === 'weekly' && (
                        <Form.Item
                            label={<span className='text-fit-red-900 font-semibold'>Dia da semana:</span>}
                            name="day_of_week"
                            className="w-full m-0"
                            rules={[{ required: true, message: 'Selecione o dia da semana.' }]}
                        >
                            <Select placeholder="Selecione o dia">
                                {DAYS_OF_WEEK.map(d => (
                                    <Option key={d.value} value={d.value}>{d.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    {scheduleType === 'specific' && (
                        <Form.Item
                            label={<span className='text-fit-red-900 font-semibold'>Data:</span>}
                            name="specific_date"
                            className="w-full m-0"
                            rules={[{ required: true, message: 'Selecione a data.' }]}
                        >
                            <DatePicker
                                className='w-full'
                                format="DD/MM/YYYY"
                                placeholder="Selecione a data"
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        label={<span className='text-fit-red-900 font-semibold'>Horário:</span>}
                        name="scheduled_time"
                        className="w-full m-0"
                        rules={[{ required: true, message: 'Selecione o horário.' }]}
                    >
                        <Select
                            placeholder="Selecione o horário"
                            showSearch
                            optionFilterProp="children"
                        >
                            {TIME_OPTIONS.map(t => (
                                <Option key={t} value={t}>{t}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <div className='flex flex-col gap-1'>
                                <span className='text-fit-red-900 font-semibold'>Usuários:</span>
                                <span className='text-gray-400 text-xs font-normal'>
                                    Deixe em branco para enviar para todos os usuários
                                </span>
                            </div>
                        }
                        name="users"
                        className="w-full m-0"
                    >
                        <Select
                            className='w-full'
                            mode="multiple"
                            placeholder="Todos os usuários"
                            optionFilterProp="children"
                            showSearch
                        >
                            {usersList.map(user => (
                                <Option key={user.id} value={`${user.id}|${user.full_name}`}>
                                    {user.full_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={<span className='text-fit-red-900 font-semibold'>Ativo:</span>}
                        name="is_active"
                        valuePropName="checked"
                        className="w-full m-0"
                    >
                        <Switch checkedChildren="Sim" unCheckedChildren="Não" />
                    </Form.Item>

                </div>
            </Form>
        </Modal>
    )
}
